import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from "react";
import { toast } from "sonner";
import { openStoredFile } from "@/lib/storage-url";

type Props = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "download"
> & {
  /** The stored value: a Storage object URL, or a bare path in `bucket`. */
  value: string;
  /** The bucket a bare path lives in. A full URL names its own. */
  bucket: string;
  /** Sign for download (Content-Disposition: attachment), naming the file when given. */
  download?: string | boolean;
  /** Toast shown when the file can't be opened; translated by the caller. */
  errorText: string;
};

/**
 * An `<a>` to a file in a bucket that is going private (COL-436).
 *
 * The href is always `#`: a stored value is text somebody wrote into a row
 * and must not reach an href unchecked — and any real URL there would also be
 * navigable by routes that bypass `onClick` entirely (middle-click, "Open
 * link in new tab"), serving the stored public URL that dies when the bucket
 * flips. The click is the only way the file opens: a tab is opened inside the
 * gesture and pointed at a fresh signed URL (`openStoredFile`).
 */
export const StoredFileLink = forwardRef<HTMLAnchorElement, Props>(
  function StoredFileLink(
    { value, bucket, download, errorText, onClick, children, ...rest },
    ref
  ) {
    return (
      <a
        ref={ref}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}
        href="#"
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          onClick?.(e);
          e.preventDefault();
          void openStoredFile(
            value,
            bucket,
            download ? { download } : undefined
          ).then((ok) => {
            if (!ok) toast.error(errorText);
          });
        }}
      >
        {children}
      </a>
    );
  }
);
